import * as React from 'react';
import * as monaco from 'monaco-editor';
import { editor } from 'monaco-editor/esm/vs/editor/editor.api';
import { identity, uniqueId } from 'lodash';
import { API } from '@config/api';
import { expandTypeName, getAgentId, getRawCodeTypeName, getUserId } from '@lib/quick-func';
import { FunctionParameter } from '@interface/data-query';
import './sql-input.less';

type Position = monaco.Position;
const MCompletionKind = monaco.languages.CompletionItemKind;

enum BCompletionItemKind { // defined from backend
  TextCompletion = 1,
  MethodCompletion = 2,
  FunctionCompletion = 3,
  ConstructorCompletion = 4,
  FieldCompletion = 5,
  VariableCompletion = 6,
  ClassCompletion = 7,
  InterfaceCompletion = 8,
  ModuleCompletion = 9,
  PropertyCompletion = 10,
  UnitCompletion = 11,
  ValueCompletion = 12,
  EnumCompletion = 13,
  KeywordCompletion = 14,
  SnippetCompletion = 15,
  ColorCompletion = 16,
  FileCompletion = 17,
  ReferenceCompletion = 18,
  FolderCompletion = 19,
  EnumMemberCompletion = 20,
  ConstantCompletion = 21,
  StructCompletion = 22,
  EventCompletion = 23,
  OperatorCompletion = 24,
  TypeParameterCompletion = 25
}

function inputSessionId() {
  return getAgentId() + getUserId();
}

function convertCompletionKind(res: BCompletionItemKind): monaco.languages.CompletionItemKind {
  switch (res) {
    case BCompletionItemKind.TextCompletion:
      return MCompletionKind.Text;
    case BCompletionItemKind.MethodCompletion:
      return MCompletionKind.Method;
    case BCompletionItemKind.FunctionCompletion:
      return MCompletionKind.Function;
    case BCompletionItemKind.ConstructorCompletion:
      return MCompletionKind.Constructor;
    case BCompletionItemKind.FieldCompletion:
      return MCompletionKind.Field;
    case BCompletionItemKind.VariableCompletion:
      return MCompletionKind.Variable;
    case BCompletionItemKind.ClassCompletion:
      return MCompletionKind.Class;
    case BCompletionItemKind.InterfaceCompletion:
      return MCompletionKind.Interface;
    case BCompletionItemKind.ModuleCompletion:
      return MCompletionKind.Module;
    case BCompletionItemKind.PropertyCompletion:
      return MCompletionKind.Property;
    case BCompletionItemKind.UnitCompletion:
      return MCompletionKind.Unit;
    case BCompletionItemKind.ValueCompletion:
      return MCompletionKind.Value;
    case BCompletionItemKind.EnumCompletion:
      return MCompletionKind.Enum;
    case BCompletionItemKind.KeywordCompletion:
      return MCompletionKind.Keyword;
    case BCompletionItemKind.SnippetCompletion:
      return MCompletionKind.Snippet;
    case BCompletionItemKind.ColorCompletion:
      return MCompletionKind.Color;
    case BCompletionItemKind.FileCompletion:
      return MCompletionKind.File;
    case BCompletionItemKind.ReferenceCompletion:
      return MCompletionKind.Reference;
    case BCompletionItemKind.FolderCompletion:
      return MCompletionKind.Folder;
    case BCompletionItemKind.EnumMemberCompletion:
      return MCompletionKind.EnumMember;
    case BCompletionItemKind.ConstantCompletion:
      return MCompletionKind.Constant;
    case BCompletionItemKind.StructCompletion:
      return MCompletionKind.Struct;
    case BCompletionItemKind.EventCompletion:
      return MCompletionKind.Event;
    case BCompletionItemKind.OperatorCompletion:
      return MCompletionKind.Operator;
    case BCompletionItemKind.TypeParameterCompletion:
      return MCompletionKind.TypeParameter;
    default:
      return MCompletionKind.Text;
  }
}

function sqlInput({ sql, onSqlChange, changeOnBlur, rawCodeMethodName, rawCodeReturn, rawCodeParams, returnMultiValue, returnNullable }: {
  sql: string;
  onSqlChange: (next: string) => void;
  changeOnBlur?: boolean;
  rawCodeMethodName?: string;
  rawCodeParams?: FunctionParameter[];
  rawCodeReturn?: any;
  returnMultiValue?: boolean;
  returnNullable?: boolean;
}) {

  const ideId = uniqueId();
  let ide: editor.IStandaloneCodeEditor;

  async function createDependencyProposals(range: any, ideId: string, text: string, position: monaco.Position) {

    try {
      const res: any = await API.common.getIdeSuggestions(ideId, encodeURIComponent(text), position.lineNumber, position.column,
        'pgsql',
        undefined,
        getRawCodeTypeName(rawCodeReturn),
        rawCodeParams && encodeURIComponent(JSON.stringify(rawCodeParams.map((item: FunctionParameter) => ({
          name: item.name,
          type: item.type && expandTypeName(item.type.name),
          multi_value: item.multiValue,
          nullable: item.nullable
        })))),
        undefined,
        rawCodeMethodName,
        undefined,
        returnMultiValue,
        returnNullable
      );
      const suggestions: monaco.languages.CompletionItem[] = [];

      for (const s of res) {
        suggestions.push({
          range,
          label: s.label,
          kind: convertCompletionKind(s.kind),
          insertText: s.insertText || s.label,
          filterText: s.filterText,
          detail: s.detail,
          insertTextRules: s.insertTextFormat === 'Snippet' && monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet || undefined
        });
      }

      return suggestions;
    } catch (e: any) {
      throw e;
    }
  }

  let completion: any;

  React.useEffect(() => {
    ide = monaco.editor.create(document.getElementById(ideId) as any, {
      value: sql,
      language: 'pgsql',
      theme: 'vs',
      automaticLayout: true,
      minimap: {
        enabled: false
      },
      padding: { top: 12 },
      fontSize: 14,
      lineNumbers: 'off'
    });


    completion = monaco.languages.registerCompletionItemProvider('pgsql', {
      triggerCharacters: ['\n'],
      async provideCompletionItems(model: any, position: Position, context: any) {
        const text = model.getValue();

        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        };

        try {
          const res = await createDependencyProposals(range, inputSessionId(), text, position
          );
          return {
            suggestions: res
          };
        } catch (e: any) {
          // do nothing
        }

        return {
          suggestions: []
        };
      }
    });

    ide.onDidBlurEditorText((event: any) => {
      if (changeOnBlur) {
        const newValue = ide.getValue();
        onSqlChange(newValue);
      }
    });

    ide.setValue(sql);

    ide.onDidChangeModelContent((event: any) => {
      const newValue = ide.getValue();

      if (!changeOnBlur) {
        onSqlChange(newValue);
      }
    });

    return () => {
      API.common.endIde(inputSessionId());
      if (ide && ide.dispose) {
        ide.dispose();
      }
      if (completion) {
        completion.dispose();
      }
    };
  }, []);

  React.useEffect(() => {
    if (ide) {
      ide.setValue(sql);
    }

  }, [sql]);

  return <div id={ideId} className="ide framely-sql-ide" style={{ minHeight: 200 }} />;
}

export const SqlInput = sqlInput;
